import { Component, ViewChild } from '@angular/core';

import { lastValueFrom } from 'rxjs';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { NzTabChangeEvent } from 'ng-zorro-antd/tabs';
import { AnyoSortDirection } from 'projects/app-core/src/models/gridRequest';
import {
  ExpertClientsGridRequest,
  ExpertClientsGridResponse,
  ExpertClientsGridResponseData,
  ExpertClientsSort,
  ExpertClientsTab,
} from '../../../models/experts/clientGridReqRes';
import { ExpertsService } from '../../../services/experts.service';
import { TableComponent } from './table/table.component';
import { ActivatedRoute } from '@angular/router';
import { CreateExpertTherapistListenerRequest } from '../../../models/createExpertTherapistListener';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import { AnyoTableComponent } from 'projects/app-core/src/app/models/AnyoTableComponent';
import { IAnyoUser } from 'projects/app-core/src/auth/models/IAnyoUser';

@Component({
  selector: 'app-clients',
  templateUrl: './clients.component.html',
  styleUrl: './clients.component.scss',
})
export class ClientsComponent extends AnyoTableComponent<
  ExpertClientsGridResponseData,
  ExpertClientsGridRequest
> {
  expertClientSpinner!: boolean;
  globalSearchInput = '';
  tableData: ExpertClientsGridResponse = {
    data: [],
    filterOptions: {
      partners: [],
    },
    pageNum: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
  };
  tabs = [ExpertClientsTab.ACTIVE_CLIENTS, ExpertClientsTab.INACTIVE_CLIENTS];
  selectedTab: number = 0;
  expertId = '';

  constructor(
    private expert: ExpertsService,
    private toast: ToastService,
    override auth: AuthService,
    private activatedRoute: ActivatedRoute,
  ) {
    super(auth, toast);
  }

  @ViewChild('activeTableComponent')
  activeTableComponent?: TableComponent;
  @ViewChild('inactiveTableComponent')
  inactiveTableComponent?: TableComponent;

  async listExpertClients() {
    try {
      this.expertClientSpinner = true;
      this.tableData = await lastValueFrom(
        this.expert.getExpertClientsGrid(this.gridRequest!),
      );
      this.totalCount = this.tableData.totalRecords;
      this.expertClientSpinner = false;
      if (this.activeTableComponent) {
        this.activeTableComponent.setPartnerFilterData(
          this.tableData.filterOptions,
        );
      }
      if (this.inactiveTableComponent) {
        this.inactiveTableComponent.setPartnerFilterData(
          this.tableData.filterOptions,
        );
      }
    } catch (e) {
      this.expertClientSpinner = false;
      const errorBody = (e as unknown as { error: IAnyoError })
        .error as IAnyoError;
      this.toast.showAnyoErrorToast(errorBody.description);
    }
  }

  async searchGlobal() {
    this.gridRequest!.globalSearch = [this.globalSearchInput];
    await this.listExpertClients();
  }
  override async ready(user?: IAnyoUser): Promise<void> {
    if (user) {
      const expertId = this.activatedRoute.snapshot.params['expertId'];
      this.expertId = expertId;
      this.getExpertDetails();
      this.gridRequest = {
        expertId: expertId,
        dateFilters: undefined,
        export: false,
        filters: {
          PARTNER: [],
          TEST_USER: ['false'],
        },
        from: undefined,
        globalSearch: [],
        page: 1,
        pageSize: this.pageSize,
        searchQuery: undefined,
        tab: ExpertClientsTab.ACTIVE_CLIENTS,
        to: undefined,
        sort: undefined,
      };
      await this.getData();
    } else {
      this.data = [];
    }
    return Promise.resolve();
  }
  async getData() {
    await this.listExpertClients();
  }
  expertDetail: CreateExpertTherapistListenerRequest | undefined;
  getExpertDetails() {
    this.expert.getExpertTherapistListener(this.expertId).subscribe({
      next: (value) => {
        this.expertDetail = value;
      },
      error: (error) => {
        const errorBody = error.error as IAnyoError;
        this.toast.showAnyoErrorToast(errorBody.description);
      },
    });
  }

  override async resetFilter() {
    this.globalSearchInput = '';
    this.gridRequest = {
      filters: {
        PARTNER: [],
        TEST_USER: ['false'],
      },
      expertId: this.expertId,
      export: false,
      globalSearch: [],
      page: 1,
      pageSize: this.pageSize,
      tab: this.tabs[this.selectedTab],
    };
    if (this.activeTableComponent) {
      this.activeTableComponent.resetValues();
    }
    if (this.inactiveTableComponent) {
      this.inactiveTableComponent.resetValues();
    }
    await this.listExpertClients();
  }

  async tabChange($event: NzTabChangeEvent) {
    this.selectedTab = $event.index!;
    this.tableData = {
      data: [],
      filterOptions: {
        partners: [],
      },
      pageNum: 1,
      pageSize: 10,
      totalPages: 0,
      totalRecords: 0,
    };

    this.globalSearchInput = '';
    this.gridRequest = {
      export: false,
      expertId: this.expertId,
      globalSearch: [],
      page: 1,
      pageSize: this.pageSize,
      tab: this.tabs[this.selectedTab],
    };
    if (this.activeTableComponent) {
      this.activeTableComponent.setPartnerFilterData(
        this.tableData.filterOptions,
      );
    }
    if (this.inactiveTableComponent) {
      this.inactiveTableComponent.setPartnerFilterData(
        this.tableData.filterOptions,
      );
    }
    if ($event.index == 0) {
      this.gridRequest!.tab = ExpertClientsTab.ACTIVE_CLIENTS;
    }
    if ($event.index == 1) {
      this.gridRequest!.tab = ExpertClientsTab.INACTIVE_CLIENTS;
    }
    await this.listExpertClients();
  }

  async expertClientSort($event: {
    sort: string | null;
    expertClients: ExpertClientsSort;
  }) {
    // Initialize sort with all keys
    if (!this.gridRequest?.sort) {
      this.gridRequest!.sort = {
        NAME: undefined,
        EMAIL: undefined,
        TOTAL_PLANS: undefined,
        TOTAL_PACKS: undefined,
        ACTIVE_PLANS: undefined,
        ACTIVE_PACKS: undefined,
        COMPLETED_PLANS: undefined,
        COMPLETED_PACKS: undefined,
      };
    }

    // Now type-safe assignment
    const sortKey: ExpertClientsSort = $event.expertClients;

    if ($event.sort) {
      this.gridRequest!.sort[sortKey] =
        $event.sort === 'ascend'
          ? AnyoSortDirection.ASC
          : AnyoSortDirection.DESC;
    } else {
      // Reset to undefined instead of deleting
      this.gridRequest!.sort[sortKey] = undefined;
    }

    await this.listExpertClients();
  }

  async partnerFilter($event: string[]) {
    if (!this.gridRequest!.filters) {
      this.gridRequest!.filters = {
        PARTNER: [],
        TEST_USER: [],
      };
    }
    if ($event && $event.length) {
      this.gridRequest!.filters!.PARTNER = $event;
      await this.listExpertClients();
    }
  }

  async testFilter($event: boolean) {
    if (!this.gridRequest!.filters) {
      this.gridRequest!.filters = {
        PARTNER: [],
        TEST_USER: [],
      };
    }
    if (this.gridRequest) {
      this.gridRequest!.filters!.TEST_USER = [$event.toString()];
      await this.listExpertClients();
    }
  }
}
